import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/authService';

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            navigate('/forgot-password');
        }
    }, [token, navigate]);

    const validatePassword = (pwd: string) => {
        if (pwd.length < 6) return 'Password must be at least 6 characters!';
        if (pwd.length > 10) return 'Password must not exceed 10 characters!';
        if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one capital letter!';
        if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number!';
        if (!/[^A-Za-z0-9]/.test(pwd)) return 'Password must contain at least one special character!';
        return '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match!');
            return;
        }

        const validationError = validatePassword(password);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        try {
            await resetPassword(token!, password);
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Reset failed!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 md:px-6">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/">
                        <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                            TubeScript AI
                        </span>
                    </Link>
                    <p className="text-gray-400 mt-2">Create your new password</p>
                </div>

                <div className="bg-gray-800 rounded-2xl p-6 md:p-8 border border-gray-700">
                    {success ? (
                        <div className="text-center">
                            <p className="text-5xl mb-4">✅</p>
                            <h2 className="text-xl font-bold mb-2">Password Reset!</h2>
                            <p className="text-gray-400 mb-6">
                                Your password has been reset successfully!
                                Redirecting to login in 3 seconds...
                            </p>
                            <Link to="/login" className="text-purple-400 hover:text-purple-300 transition">
                                Go to Login
                            </Link>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            required
                                            maxLength={10}
                                            className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 pr-12 rounded-xl focus:outline-none focus:border-purple-500 transition"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition">
                                            {showPassword ? '🙈' : '👁️'}
                                        </button>
                                    </div>

                                    {password && (
                                        <ul className="mt-2 space-y-1">
                                            {[
                                                { check: password.length >= 6 && password.length <= 10, label: 'Between 6-10 characters' },
                                                { check: /[A-Z]/.test(password), label: 'One capital letter' },
                                                { check: /[0-9]/.test(password), label: 'One number' },
                                                { check: /[^A-Za-z0-9]/.test(password), label: 'One special character' },
                                            ].map((req) => (
                                                <li key={req.label} className={`text-xs flex items-center gap-1 ${req.check ? 'text-green-400' : 'text-gray-500'}`}>
                                                    {req.check ? '✅' : '⭕'} {req.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-gray-300 text-sm font-medium mb-2">
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="••••••••"
                                            required
                                            maxLength={10}
                                            className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 pr-12 rounded-xl focus:outline-none focus:border-purple-500 transition"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition">
                                            {showConfirmPassword ? '🙈' : '👁️'}
                                        </button>
                                    </div>
                                    {confirmPassword && (
                                        <p className={`text-xs mt-1 ${password === confirmPassword ? 'text-green-400' : 'text-red-400'}`}>
                                            {password === confirmPassword ? '✅ Passwords match' : '❌ Passwords do not match'}
                                        </p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition">
                                    {loading ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;


